import type { TaxYearBasedProjectPlanRecord } from '~/types';

/**
 * TaxYearPlanHelper - Shared utilities for tax year plan analysis and calculations
 *
 * These functions are used across the application to extract information from tax year plans,
 * perform calculations, and support decision-making processes.
 */
export class TaxYearPlanHelper {
  /**
   * Get total revenue across all years in the tax year plan
   */
  static getTotalRevenue(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    return taxYearPlan.reduce((sum, record) => sum + record.totalRevenue, 0);
  }

  /**
   * Get total existing debt across all years in the tax year plan
   */
  static getTotalExistingDebt(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    return taxYearPlan.reduce((sum, record) => sum + record.existingDebt, 0);
  }

  /**
   * Get total net revenue across all years in the tax year plan
   */
  static getTotalNetRevenue(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    return taxYearPlan.reduce((sum, record) => sum + record.netRevenue, 0);
  }

  /**
   * Get total bond payments across all years in the tax year plan
   */
  static getTotalBondPayments(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    return taxYearPlan.reduce((sum, record) => sum + record.totalBondPayments, 0);
  }

  /**
   * Get total payments (existing debt + bond payments) across all years
   */
  static getTotalPayments(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    return taxYearPlan.reduce((sum, record) => sum + record.totalPayment, 0);
  }

  /**
   * Get total DRF adjustments across all years
   */
  static getTotalDrfAdjustment(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    return taxYearPlan.reduce((sum, record) => sum + record.drfAdjustment, 0);
  }

  /**
   * Get final DRF balance (from last year)
   */
  static getFinalDrfBalance(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    const sortedRecords = TaxYearPlanHelper.getSortedRecords(taxYearPlan);
    if (sortedRecords.length === 0) return 0;

    return sortedRecords[sortedRecords.length - 1].drfEndingBalance;
  }

  /**
   * Get tax year plan records sorted by tax year
   */
  static getSortedRecords(
    taxYearPlan: TaxYearBasedProjectPlanRecord[]
  ): TaxYearBasedProjectPlanRecord[] {
    return [...taxYearPlan].sort((a, b) => a.taxYear - b.taxYear);
  }

  /**
   * Get tax year plan record for a specific year
   */
  static getRecordForYear(
    taxYearPlan: TaxYearBasedProjectPlanRecord[],
    taxYear: number
  ): TaxYearBasedProjectPlanRecord | undefined {
    return taxYearPlan.find((record) => record.taxYear === taxYear);
  }

  /**
   * Get years with negative DRF ending balance
   */
  static getNegativeDrfBalanceYears(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number[] {
    return taxYearPlan
      .filter((record) => record.drfEndingBalance < 0)
      .map((record) => record.taxYear)
      .sort((a, b) => a - b);
  }

  /**
   * Get years with positive DRF ending balance
   */
  static getPositiveDrfBalanceYears(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number[] {
    return taxYearPlan
      .filter((record) => record.drfEndingBalance > 0)
      .map((record) => record.taxYear)
      .sort((a, b) => a - b);
  }

  /**
   * Get years with DRF adjustments (positive or negative)
   */
  static getYearsWithDrfAdjustments(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number[] {
    return taxYearPlan
      .filter((record) => Math.abs(record.drfAdjustment) > 0.01) // Allow for small floating point differences
      .map((record) => record.taxYear)
      .sort((a, b) => a - b);
  }

  /**
   * Calculate bond payment ratio (bond payments / total revenue)
   */
  static calculateBondPaymentRatio(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    const totalRevenue = TaxYearPlanHelper.getTotalRevenue(taxYearPlan);
    const totalBondPayments = TaxYearPlanHelper.getTotalBondPayments(taxYearPlan);

    if (totalRevenue === 0) return 0;
    return totalBondPayments / totalRevenue;
  }

  /**
   * Calculate total payment ratio (all payments / total revenue)
   */
  static calculateTotalPaymentRatio(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    const totalRevenue = TaxYearPlanHelper.getTotalRevenue(taxYearPlan);
    const totalPayments = TaxYearPlanHelper.getTotalPayments(taxYearPlan);

    if (totalRevenue === 0) return 0;
    return totalPayments / totalRevenue;
  }

  /**
   * Calculate average annual payments
   */
  static getAverageAnnualPayments(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    if (taxYearPlan.length === 0) return 0;

    const totalPayments = TaxYearPlanHelper.getTotalPayments(taxYearPlan);
    return totalPayments / taxYearPlan.length;
  }

  /**
   * Calculate financial health summary
   */
  static calculateFinancialSummary(taxYearPlan: TaxYearBasedProjectPlanRecord[]): {
    totalRevenue: number;
    totalExistingDebt: number;
    totalNetRevenue: number;
    totalBondPayments: number;
    totalPayments: number;
    totalDrfAdjustment: number;
    finalDrfBalance: number;
    bondPaymentRatio: number;
    totalPaymentRatio: number;
    negativeBalanceYears: number;
    isFinanciallyViable: boolean;
  } {
    const negativeBalanceYears = TaxYearPlanHelper.getNegativeDrfBalanceYears(taxYearPlan);
    const finalDrfBalance = TaxYearPlanHelper.getFinalDrfBalance(taxYearPlan);
    const totalNetRevenue = TaxYearPlanHelper.getTotalNetRevenue(taxYearPlan);

    return {
      totalRevenue: TaxYearPlanHelper.getTotalRevenue(taxYearPlan),
      totalExistingDebt: TaxYearPlanHelper.getTotalExistingDebt(taxYearPlan),
      totalNetRevenue,
      totalBondPayments: TaxYearPlanHelper.getTotalBondPayments(taxYearPlan),
      totalPayments: TaxYearPlanHelper.getTotalPayments(taxYearPlan),
      totalDrfAdjustment: TaxYearPlanHelper.getTotalDrfAdjustment(taxYearPlan),
      finalDrfBalance,
      bondPaymentRatio: TaxYearPlanHelper.calculateBondPaymentRatio(taxYearPlan),
      totalPaymentRatio: TaxYearPlanHelper.calculateTotalPaymentRatio(taxYearPlan),
      negativeBalanceYears: negativeBalanceYears.length,
      isFinanciallyViable: finalDrfBalance >= 0 && negativeBalanceYears.length === 0,
    };
  }

  /**
   * Get the project timeline span from tax year plan
   */
  static getTimelineSpan(taxYearPlan: TaxYearBasedProjectPlanRecord[]): {
    startYear: number;
    endYear: number;
    totalYears: number;
  } {
    const sortedRecords = TaxYearPlanHelper.getSortedRecords(taxYearPlan);
    if (sortedRecords.length === 0) {
      const currentYear = new Date().getFullYear();
      return { startYear: currentYear, endYear: currentYear, totalYears: 0 };
    }

    const startYear = sortedRecords[0].taxYear;
    const endYear = sortedRecords[sortedRecords.length - 1].taxYear;

    return {
      startYear,
      endYear,
      totalYears: sortedRecords.length,
    };
  }

  /**
   * Calculate revenue utilization rate (net revenue used for bond payments)
   */
  static calculateRevenueUtilizationRate(taxYearPlan: TaxYearBasedProjectPlanRecord[]): number {
    const totalNetRevenue = TaxYearPlanHelper.getTotalNetRevenue(taxYearPlan);
    const totalBondPayments = TaxYearPlanHelper.getTotalBondPayments(taxYearPlan);

    if (totalNetRevenue === 0) return 0;
    return totalBondPayments / totalNetRevenue;
  }

  /**
   * Check if tax year plan is valid and complete
   */
  static isValidPlan(taxYearPlan: TaxYearBasedProjectPlanRecord[]): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!taxYearPlan || taxYearPlan.length === 0) {
      errors.push('Tax year plan is empty');
      return { isValid: false, errors };
    }

    // Check for required fields and valid data
    for (const record of taxYearPlan) {
      if (!record.taxYear || record.taxYear < 2000 || record.taxYear > 2100) {
        errors.push(`Invalid tax year: ${record.taxYear}`);
      }

      if (record.totalRevenue < 0) {
        errors.push(`Negative total revenue in year ${record.taxYear}: ${record.totalRevenue}`);
      }

      if (record.existingDebt < 0) {
        errors.push(`Negative existing debt in year ${record.taxYear}: ${record.existingDebt}`);
      }

      if (record.totalBondPayments < 0) {
        errors.push(
          `Negative total bond payments in year ${record.taxYear}: ${record.totalBondPayments}`
        );
      }

      if (record.totalPayment < 0) {
        errors.push(`Negative total payment in year ${record.taxYear}: ${record.totalPayment}`);
      }
    }

    // Check for duplicate years
    const years = taxYearPlan.map((record) => record.taxYear);
    const uniqueYears = new Set(years);
    if (years.length !== uniqueYears.size) {
      errors.push('Duplicate tax years found in plan');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format currency for display
   */
  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  /**
   * Format millage for display
   */
  static formatMillage(value: number): string {
    return `${value.toFixed(2)}`;
  }

  /**
   * Format percentage for display
   */
  static formatPercentage(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Format number with commas for display
   */
  static formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
  }
}
